const { createSlice, createAsyncThunk } = require('@reduxjs/toolkit');

import { newsFlashService } from '../services/newsFlash.service';
import { LoadingStatus } from 'GeminiViewerComponent/helpers/AsyncStatus';

const initialState = {
    allNewsFlashData: [],
    newsFlashData: null,
    newsFlashStatus: LoadingStatus.Idle,
    newsFlashDisplayOptionStatus: LoadingStatus.Idle,
    errors: null,
    newsFlashPageInfo: {},
    newsFlashLoadedRowsMap: {},
    newsFlashDisplayOptions: [],
    viewer: {
        modalIsOpen: false,
        activeNewsFlash: {},
    },
};

export const fetchAllNewsFlashPage = createAsyncThunk(
    'newsFlash/fetchAllNewsFlashPage',
    async () => await newsFlashService.getAll()
);

export const fetchAllNewsFlash = createAsyncThunk(
    'newsFlash/fetchAllNewsFlash',
    async (params) =>
        await newsFlashService.getAllByRange(
            params.startIndex ?? 0,
            params.stopIndex ?? 0,
            params.searchString,
            params.sort,
            params.startDate,
            params.endDate,
            params.displayOptionId,
            params.activeOnly
        )
);

export const fetchNewsFlashById = createAsyncThunk(
    'newsFlash/fetchNewsFlashById',
    async (params) => await newsFlashService.getById(params)
);

export const fetchNewsFlashDisplayOptions = createAsyncThunk(
    'newsFlash/fetchNewsFlashDisplayOptions',
    async () => await newsFlashService.getDisplayOptions()
);

export const createNewsFlash = createAsyncThunk(
    'newsFlash/createNewsFlash',
    async (params) => await newsFlashService.create(params)
);

export const updateNewsFlash = createAsyncThunk(
    'newsFlash/updateNewsFlash',
    async (params) => await newsFlashService.update(params?.news_flash_id, params)
);

export const deleteNewsFlash = createAsyncThunk(
    'newsFlash/deleteNewsFlash',
    async (params) => await newsFlashService.delete(params)
);

export const deleteNewsFlashArray = createAsyncThunk(
    'newsFlash/deleteNewsFlashArray',
    async (params) => await newsFlashService.deleteArray(params)
);

const newsFlashSlice = createSlice({
    name: 'newsFlash',
    initialState,
    reducers: {
        resetNewsFlashData: (state) => {
            return initialState;
        },
        setNewsFlashStatus: (state, action) => {
            state.newsFlashStatus = action.payload;
        },
        resetNewsFlashStatus: (state) => {
            state.newsFlashStatus = LoadingStatus.Idle;
        },
        setViewerActiveNewsFlash: (state, action) => {
            state.viewer.activeNewsFlash = action.payload;
        },
        setViewerNewsFlashModal: (state, action) => {
            state.viewer.modalIsOpen = action.payload;
        },
        clearViewerNewsFlashOnLogout: (state) => {
            state.viewer = initialState.viewer;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAllNewsFlashPage.pending, (state) => {
                state.newsFlashStatus = LoadingStatus.Loading;
            })
            .addCase(fetchAllNewsFlashPage.fulfilled, (state, action) => {
                state.newsFlashStatus = LoadingStatus.Loaded;
                state.allNewsFlashData = action.payload;
            })
            .addCase(fetchAllNewsFlash.pending, (state) => {
                state.newsFlashStatus = LoadingStatus.Loading;
            })
            .addCase(fetchAllNewsFlash.fulfilled, (state, action) => {
                state.newsFlashStatus = LoadingStatus.Loaded;
                state.newsFlashPageInfo = action.payload.pageInfo;

                if (action.meta.arg.reset) {
                    state.newsFlashLoadedRowsMap = {};
                }

                const startIndex = action.meta.arg.startIndex;
                const stopIndex = startIndex + action.payload.newsFlashData.length - 1;

                for (var i = startIndex; i <= stopIndex; i++) {
                    state.newsFlashLoadedRowsMap[i] = LoadingStatus.Loaded;
                }

                // In case we didn't load as many as requested
                for (let j = stopIndex + 1; j <= action.payload?.pageInfo?.TotalCount; j++) {
                    delete state.newsFlashLoadedRowsMap[j];
                }

                if (action.meta.arg.reset) {
                    state.allNewsFlashData = action.payload.newsFlashData;
                } else {
                    state.allNewsFlashData = state.allNewsFlashData.concat(action.payload.newsFlashData);
                }
            })
            .addCase(fetchAllNewsFlash.rejected, (state, action) => {
                state.newsFlashStatus = LoadingStatus.Failed;
                state.errors = action.error.message;
            })
            .addCase(fetchNewsFlashById.fulfilled, (state, action) => {
                state.newsFlashStatus = LoadingStatus.Loaded;
                state.newsFlashData = action.payload;
            })
            .addCase(fetchNewsFlashById.rejected, (state, action) => {
                state.newsFlashStatus = LoadingStatus.Failed;
                state.errors = action.error.message;
            })
            .addCase(fetchNewsFlashDisplayOptions.pending, (state, action) => {
                state.newsFlashDisplayOptionStatus = LoadingStatus.Loading;
            })
            .addCase(fetchNewsFlashDisplayOptions.fulfilled, (state, action) => {
                state.newsFlashDisplayOptionStatus = LoadingStatus.Loaded;
                state.newsFlashDisplayOptions = action.payload?.news_flash_display_options;
            })
            .addCase(fetchNewsFlashDisplayOptions.rejected, (state, action) => {
                state.newsFlashDisplayOptionStatus = LoadingStatus.Failed;
                state.errors = action.error.message;
            })
            .addCase(createNewsFlash.fulfilled, (state, action) => {
                state.newsFlashStatus = LoadingStatus.Idle;
                state.newsFlashData = action.payload;
            })
            .addCase(createNewsFlash.rejected, (state, action) => {
                state.newsFlashStatus = LoadingStatus.Failed;
                state.errors = action.error.message;
            })
            .addCase(updateNewsFlash.fulfilled, (state, action) => {
                state.newsFlashStatus = LoadingStatus.Idle;
                state.newsFlashData = action.payload;
            })
            .addCase(updateNewsFlash.rejected, (state, action) => {
                state.newsFlashStatus = LoadingStatus.Failed;
                state.errors = action.error.message;
            })
            .addCase(deleteNewsFlash.fulfilled, (state) => {
                state.newsFlashStatus = LoadingStatus.Idle;
            })
            .addCase(deleteNewsFlash.rejected, (state, action) => {
                state.newsFlashStatus = LoadingStatus.Failed;
                state.error = action.error.message;
            })
            .addCase(deleteNewsFlashArray.fulfilled, (state) => {
                state.newsFlashStatus = LoadingStatus.Idle;
            })
            .addCase(deleteNewsFlashArray.rejected, (state, action) => {
                state.newsFlashStatus = LoadingStatus.Failed;
                state.error = action.error.message;
            });
    },
});

export const getAllNewsFlash = (state) => state.newsFlash.allNewsFlashData;
export const getNewsFlashData = (state) => state.newsFlash.newsFlashData;
export const getNewsFlashDisplayOptions = (state) => state.newsFlash.newsFlashDisplayOptions;
export const getSessionErrors = (state) => state.newsFlash.errors;
export const getNewsFlashStatus = (state) => state.newsFlash.newsFlashStatus;
export const getNewsFlashDisplayOptionStatus = (state) => state.newsFlash.newsFlashDisplayOptionStatus;
export const getNewsFlashPageInfo = (state) => state.newsFlash.newsFlashPageInfo;
export const getNewsFlashLoadedRowsMap = (state) => state.newsFlash.newsFlashLoadedRowsMap;
export const getViewerNewsFlashModalIsOpen = (state) => state.newsFlash.viewer.modalIsOpen;
export const getActiveNewsFlash = (state) => state.newsFlash.viewer.activeNewsFlash;
export const getQueuedNewsFlash = (state) => {
    return state.asset.queuedNewsFlashes?.[0] ?? null;
};

export const {
    resetNewsFlashData,
    setNewsFlashStatus,
    resetnewsFlashStatus,
    setViewerActiveNewsFlash,
    setViewerNewsFlashModal,
    clearViewerNewsFlashOnLogout,
} = newsFlashSlice.actions;

export default newsFlashSlice.reducer;
